import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Gitlab, Loader2, Lock, RefreshCw, Search } from 'lucide-react'
import {
  checkAppName,
  getGitLabLinkURL,
  listGitLabBranches,
  listGitLabRepos,
  type NameCheckResult,
  type GitLabBranch,
  type GitLabRepo,
} from '../lib/api'
import { appSlugFromName, finalizeAppSlug, isValidAppSlug, normalizeAppSlugInput } from '../lib/appIdentity'
import { BranchPicker } from './BranchPicker'

interface GitLabRepoBrowserProps {
  onSelect: (repo: GitLabRepo, branch: string, options?: { name?: string; display_name?: string }) => void
  onCancel: () => void
  importing?: boolean
  mode?: 'import' | 'attach'
  actionLabel?: string
  onNeedsReconnect?: () => void
}

function formatGitLabInstanceLabel(instanceUrl?: string): string {
  if (!instanceUrl) return 'your GitLab instance'

  try {
    return new URL(instanceUrl).host || 'your GitLab instance'
  } catch {
    return instanceUrl
  }
}

export default function GitLabRepoBrowser({
  onSelect,
  onCancel,
  importing = false,
  mode = 'import',
  actionLabel,
  onNeedsReconnect,
}: GitLabRepoBrowserProps) {
  const isAttachMode = mode === 'attach'
  const [repos, setRepos] = useState<GitLabRepo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [selectedRepo, setSelectedRepo] = useState<GitLabRepo | null>(null)
  const [branches, setBranches] = useState<GitLabBranch[]>([])
  const [branchesDefault, setBranchesDefault] = useState<string | null>(null)
  const [branchesHasMore, setBranchesHasMore] = useState(false)
  const [branchesPage, setBranchesPage] = useState(1)
  const [loadingMoreBranches, setLoadingMoreBranches] = useState(false)
  const [selectedBranch, setSelectedBranch] = useState('')
  const [loadingBranches, setLoadingBranches] = useState(false)
  // Server-side branch search. For repos like exante-crm (1000+ branches)
  // the dropdown would otherwise omit the actual default (`release/4.172`)
  // because the alphabetical pagination cap of 1000 cuts it off. The
  // backend now forwards this to GitLab's `?search=` param so the user
  // can find ANY branch by typing.
  const [branchSearch, setBranchSearch] = useState('')
  const [needsReconnect, setNeedsReconnect] = useState(false)
  const [instanceUrl, setInstanceUrl] = useState<string | undefined>(undefined)
  const [appTitle, setAppTitle] = useState('')
  const [slugInput, setSlugInput] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [nameCheck, setNameCheck] = useState<NameCheckResult | null>(null)
  const [checkingName, setCheckingName] = useState(false)
  const appName = finalizeAppSlug(slugInput)
  const hasAppTitle = appTitle.trim().length > 0

  const fetchRepos = useCallback(async (pageNum: number, append: boolean = false) => {
    if (append) {
      setLoadingMore(true)
    } else {
      setLoading(true)
    }
    setError(null)

    try {
      const result = await listGitLabRepos(pageNum)
      if (append) {
        setRepos((prev) => [...prev, ...result.repos])
      } else {
        setRepos(result.repos)
      }
      setPage(result.page)
      setHasMore(result.has_more)
      setInstanceUrl((prev) => result.instance_url || result.repos[0]?.instance_url || prev)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load repositories'
      const lower = message.toLowerCase()
      if (
        lower.includes('scope')
        || lower.includes('permission')
        || lower.includes('forbidden')
        || lower.includes('not connected')
        || lower.includes('reconnect')
        || lower.includes('expired')
        || lower.includes('unauthorized')
      ) {
        setNeedsReconnect(true)
        onNeedsReconnect?.()
      } else {
        setError(message)
      }
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [onNeedsReconnect])

  useEffect(() => {
    fetchRepos(1)
  }, [fetchRepos])

  useEffect(() => {
    if (!selectedRepo) {
      setBranches([])
      setSelectedBranch('')
      setBranchSearch('')
      setAppTitle('')
      setSlugInput('')
      setSlugEdited(false)
      setNameCheck(null)
      return
    }

    // Reset the search input when the repo selection changes -- a fresh
    // repo deserves a fresh search box. The next effect (which depends
    // on `selectedRepo + branchSearch`) then runs with empty search.
    setBranchSearch('')
  }, [selectedRepo])

  // Debounced branch fetch: runs when the repo selection OR search query
  // changes. 300ms debounce on the search input so we don't hammer the
  // GitLab API on every keystroke.
  useEffect(() => {
    if (!selectedRepo) return

    let cancelled = false
    const debounce = setTimeout(() => {
      if (cancelled) return
      setLoadingBranches(true)

      listGitLabBranches(selectedRepo, { search: branchSearch, page: 1 })
        .then((result) => {
          if (cancelled) return
          setBranches(result.branches)
          setBranchesDefault(result.default_branch ?? null)
          setBranchesHasMore(Boolean(result.has_more))
          setBranchesPage(1)
          // Pre-select the default branch on first load (no search). When
          // the user types, preserve their existing selection if it's
          // still in the visible list, otherwise leave it alone so they
          // can keep typing to refine.
          if (!branchSearch.trim()) {
            const defaultName =
              result.default_branch
              || result.branches.find((b) => b.default)?.name
              || selectedRepo.default_branch
            setSelectedBranch(defaultName)
          }
        })
        .catch(() => {
          if (cancelled) return
          // Fall back to the repo's own default_branch when the API
          // call fails -- import still works with manual branch input.
          setBranches([{ name: selectedRepo.default_branch, default: true }])
          setBranchesDefault(selectedRepo.default_branch)
          setBranchesHasMore(false)
          setBranchesPage(1)
          if (!branchSearch.trim()) {
            setSelectedBranch(selectedRepo.default_branch)
          }
        })
        .finally(() => {
          if (!cancelled) setLoadingBranches(false)
        })
    }, branchSearch ? 300 : 0)

    return () => {
      cancelled = true
      clearTimeout(debounce)
    }
  }, [selectedRepo, branchSearch])

  const loadMoreBranches = useCallback(() => {
    if (!selectedRepo || loadingMoreBranches || !branchesHasMore) return
    const nextPage = branchesPage + 1
    setLoadingMoreBranches(true)
    listGitLabBranches(selectedRepo, { search: branchSearch, page: nextPage })
      .then((result) => {
        // Server may return duplicates if branches were added between requests;
        // de-dupe by name so the picker never shows the same branch twice.
        setBranches((prev) => {
          const seen = new Set(prev.map((b) => b.name))
          const merged = [...prev]
          for (const branch of result.branches) {
            if (!seen.has(branch.name)) {
              merged.push(branch)
              seen.add(branch.name)
            }
          }
          return merged
        })
        setBranchesHasMore(Boolean(result.has_more))
        setBranchesPage(nextPage)
      })
      .catch(() => {
        // Keep the existing list; user can retry by reopening or refining search.
        setBranchesHasMore(false)
      })
      .finally(() => setLoadingMoreBranches(false))
  }, [selectedRepo, branchSearch, branchesPage, branchesHasMore, loadingMoreBranches])

  useEffect(() => {
    if (!selectedRepo) return
    setAppTitle(selectedRepo.name)
    setSlugInput(appSlugFromName(selectedRepo.name))
    setSlugEdited(false)
    setNameCheck(null)
  }, [selectedRepo])

  useEffect(() => {
    if (!selectedRepo || slugEdited) return
    setSlugInput(appSlugFromName(appTitle))
  }, [appTitle, selectedRepo, slugEdited])

  useEffect(() => {
    let cancelled = false
    if (isAttachMode || !selectedRepo || !isValidAppSlug(appName)) {
      setNameCheck(null)
      setCheckingName(false)
      return
    }

    setCheckingName(true)
    const timer = window.setTimeout(async () => {
      try {
        const result = await checkAppName(appName)
        if (!cancelled) {
          setNameCheck(result)
        }
      } catch {
        if (!cancelled) {
          setNameCheck(null)
        }
      } finally {
        if (!cancelled) {
          setCheckingName(false)
        }
      }
    }, 250)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [appName, selectedRepo, isAttachMode])

  const filteredRepos = searchQuery
    ? repos.filter((repo) => repo.full_name.toLowerCase().includes(searchQuery.toLowerCase()))
    : repos

  const instanceLabel = formatGitLabInstanceLabel(instanceUrl || selectedRepo?.instance_url)

  function handleLoadMore() {
    fetchRepos(page + 1, true)
  }

  function handleRetry() {
    setError(null)
    setNeedsReconnect(false)
    fetchRepos(1)
  }

  if (needsReconnect) {
    return (
      <div className="py-8 text-center space-y-4">
        <Gitlab className="w-10 h-10 text-muted-foreground mx-auto" />
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
          Your GitLab connection to {instanceLabel} needs to be updated before repositories can be {isAttachMode ? 'linked' : 'imported'}.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors text-sm"
          >
            Cancel
          </button>
          <a
            href={getGitLabLinkURL()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm inline-flex items-center gap-2"
          >
            <Gitlab className="w-4 h-4" />
            Reconnect GitLab
          </a>
        </div>
      </div>
    )
  }

  if (error && !loading) {
    return (
      <div className="py-8 text-center space-y-4">
        <AlertCircle className="w-10 h-10 text-destructive mx-auto" />
        <p className="text-sm text-destructive">{error}</p>
        <div className="flex gap-3 justify-center">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRetry}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm inline-flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="py-12 text-center space-y-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
        <p className="text-sm text-muted-foreground">Loading repositories from {instanceLabel}...</p>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors text-sm"
        >
          Cancel
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search GitLab repositories..."
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
        />
      </div>

      {filteredRepos.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-sm text-muted-foreground">
            {searchQuery
              ? `No repos matching '${searchQuery}'`
              : `No repositories found on ${instanceLabel}`}
          </p>
        </div>
      ) : (
        <div className="max-h-80 overflow-y-auto border border-border rounded-lg divide-y divide-border">
          {filteredRepos.map((repo) => (
            <button
              key={`${repo.id}:${repo.full_name}`}
              type="button"
              onClick={() => setSelectedRepo(
                selectedRepo?.id === repo.id && selectedRepo?.full_name === repo.full_name ? null : repo
              )}
              className={`w-full text-left px-3 py-2.5 hover:bg-secondary/50 transition-colors ${
                selectedRepo?.id === repo.id && selectedRepo?.full_name === repo.full_name ? 'bg-secondary' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm truncate">{repo.full_name}</span>
                {repo.private && (
                  <span className="flex items-center gap-0.5 text-xs text-amber-600 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded-full shrink-0">
                    <Lock className="w-3 h-3" />
                    Private
                  </span>
                )}
                {repo.language && (
                  <span className="text-xs text-muted-foreground shrink-0">{repo.language}</span>
                )}
              </div>
              {repo.description && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{repo.description}</p>
              )}
            </button>
          ))}
        </div>
      )}

      {hasMore && !searchQuery && (
        <div className="text-center">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="px-4 py-1.5 text-sm text-primary hover:text-primary/80 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {loadingMore ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading...
              </>
            ) : (
              'Load more'
            )}
          </button>
        </div>
      )}

      {selectedRepo && (
        <div className="border border-border rounded-lg p-3 space-y-3 bg-secondary/30">
          <div className="flex items-center gap-2 text-sm">
            <Gitlab className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="font-medium truncate">{selectedRepo.full_name}</span>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground shrink-0">Branch:</label>
            <BranchPicker
              className="flex-1"
              branches={branches}
              defaultBranchName={branchesDefault || selectedRepo.default_branch}
              value={selectedBranch}
              onChange={setSelectedBranch}
              search={branchSearch}
              onSearchChange={setBranchSearch}
              loading={loadingBranches}
              hasMore={branchesHasMore}
              onLoadMore={loadMoreBranches}
              loadingMore={loadingMoreBranches}
            />
          </div>

          {!isAttachMode && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1.5">App Name</label>
                <input
                  type="text"
                  value={appTitle}
                  onChange={(e) => setAppTitle(e.target.value)}
                  placeholder="My Project"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  The human-readable name shown in the dashboard.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">URL Slug</label>
                <input
                  type="text"
                  value={slugInput}
                  onChange={(e) => {
                    setSlugInput(normalizeAppSlugInput(e.target.value))
                    setSlugEdited(true)
                  }}
                  placeholder="my-project"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  {!appName ? 'Choose a platform app slug.' : !isValidAppSlug(appName) ? (
                    <span className="text-destructive">
                      3-50 characters, starts with a letter, lowercase letters, numbers, and hyphens only
                    </span>
                  ) : checkingName ? (
                    'Checking availability...'
                  ) : nameCheck && !nameCheck.available ? (
                    <span className="text-amber-600">
                      Taken. Suggested: <code className="bg-secondary px-1 rounded">{nameCheck.suggestion || `${appName}-2`}</code>
                      {nameCheck.suggestion && (
                        <>
                          {' '}
                          <button
                            type="button"
                            onClick={() => {
                              setSlugInput(normalizeAppSlugInput(nameCheck.suggestion || ''))
                              setSlugEdited(true)
                            }}
                            className="underline underline-offset-2 hover:text-amber-700"
                          >
                            Use suggestion
                          </button>
                        </>
                      )}
                    </span>
                  ) : (
                    <>Your app will be at <code className="bg-secondary px-1 rounded">/a/{appName}/</code></>
                  )}
                </p>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={() => onSelect(
                selectedRepo,
                selectedBranch,
                isAttachMode ? undefined : { name: appName, display_name: appTitle || undefined },
              )}
              disabled={
                !selectedBranch
                || loadingBranches
                || importing
                || (!isAttachMode && (!hasAppTitle || !isValidAppSlug(appName) || Boolean(nameCheck && !nameCheck.available)))
              }
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm inline-flex items-center justify-center gap-2"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isAttachMode ? 'Linking...' : 'Importing...'}
                </>
              ) : (
                actionLabel || (isAttachMode ? 'Link Repository' : 'Import')
              )}
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={importing}
          className="px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors text-sm disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
